import { useMemo } from 'react'
import { cn } from '@/lib/utils/helpers'
import type { DateInput, ReportDateRangePickerProps } from '../types/components'
import {
  AdvancedDateRangePicker,
  getAngularSystemAnnouncementPresets,
  type PresetRange,
} from '@/components/ui/AdvancedDateRangePicker'
import { FILTER_DATE_TRIGGER_CLASS } from '../constants/configuredReportStyles'

export function parseReportDateOnly(value: DateInput): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  const raw = String(value).trim()
  if (!raw) return null

  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (ymd) {
    const [, year, month, day] = ymd
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmy) {
    const [, day, month, year] = dmy
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function useAngularReportDatePresets(): PresetRange[] {
  return useMemo(() => getAngularSystemAnnouncementPresets(), [])
}

export function ReportDateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholder = 'Select Date Range',
  customPresets,
  hideTriggerIcon = true,
  triggerClassName = FILTER_DATE_TRIGGER_CLASS,
  dropdownPanelClassName,
}: ReportDateRangePickerProps) {
  const defaultPresets = useAngularReportDatePresets()

  return (
    <AdvancedDateRangePicker
      startDate={parseReportDateOnly(startDate)}
      endDate={parseReportDateOnly(endDate)}
      onChange={onChange}
      placeholder={placeholder}
      displayRangeFormat="angularMmddyyyy"
      presetsOnTop
      customPresets={customPresets ?? defaultPresets}
      monthsShown={2}
      stackMonthsVertically
      dropdownPanelClassName={cn(
        'w-full min-w-0 max-w-full overflow-x-hidden',
        dropdownPanelClassName,
      )}
      hideTriggerIcon={hideTriggerIcon}
      triggerClassName={triggerClassName}
    />
  )
}
